// ----------------------------------------------------------------------- react
import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";

// ----------------------------------------------------------------------- internos
import { descartar, traerComprobantes } from "../services/comprobantes";
import { marcar, traerFacturas } from "../services/facturasMail";
import { esImagen, firmarPapel } from "../services/papelComprobante";
import { traerChips } from "../services/facturasAPagar";
import { avisoAlCorregir, chipDe } from "../shared/facturasAPagar";
import { Corregir, dia } from "./Comprobantes";
import { lectura, NO_PUDE, SIN_LEER } from "../shared/facturasMail";
import {
  coincide,
  cuando,
  cuandoLlego,
  esNuevo,
  fila,
  linkVigente,
  ORDENES,
  ordenar,
  POR_LLEGADA,
} from "../shared/facturasRemitos";
import { plata } from "../shared/plata";
import { etiquetaLeidoPor } from "../shared/lectura";
import PuertaCerrada from "../components/funcionales/PuertaCerrada";
import "./css/comprobantes.css";

/* Facturas y Remitos — TODO junto, venga por donde venga.
 *
 * Nico preguntó qué diferencia había entre «Facturas y remitos» (lo que se manda
 * por WhatsApp) y «Facturas de proveedores» (lo que llega por mail). Nacho
 * (28/09): aunarlas — que sea una sola lista sin importar por dónde vinieron.
 *
 * Son dos tablas distintas (`comprobantes` = WhatsApp, por dueño; `facturas` =
 * mail, con permiso `ver.comprobantes`), así que NO se migra el dato: se leen
 * las dos, se juntan en una lista con un chip que dice de dónde vino cada una, y
 * cada fila conserva sus acciones (las de WhatsApp se corrigen/descartan, las de
 * proveedores se marcan como usadas).
 *
 * EL ACCESO SON DOS LLAVES (Igna, 07/10: «privado por default»):
 * `ver.comprobantes` entra y ve sólo lo que cargó él por WhatsApp;
 * `ver.comprobantes.todos` ve todo, lo de los demás y lo de mail. Sin ninguna
 * de las dos, la puerta. Lo recorta la base; acá sólo no se pide lo que no va
 * a venir.
 *
 * LO QUE LLEGÓ ÚLTIMO VA ARRIBA (ticket 451): la fecha de la factura es el otro
 * orden, a un toque. El porqué, en `shared/facturasRemitos`.
 */

const ROTULO_TIPO = {
  factura: "Factura", remito: "Remito", presupuesto: "Presupuesto", otro: "Otro",
};

/* `dudas` trae los nombres de los campos tal cual los escribe el bot
   (`numero`, `cuit`…). El aviso los dice con el rótulo que tienen acá al lado,
   en la misma carta; lo que no es un campo (a veces deja una frase) va como
   vino. */
const ROTULO_CAMPO = {
  tipo: "Tipo", proveedor: "Proveedor", cuit: "CUIT", fecha: "Fecha",
  numero: "Número", importe: "Importe", moneda: "Moneda", detalle: "Detalle",
};
const rotuloDeCampo = (c) => ROTULO_CAMPO[String(c).trim().toLowerCase()] || String(c);

const TIPOS = [
  ["todos", "Todos"],
  ["remito", "Remitos"],
  ["factura", "Facturas"],
  ["presupuesto", "Presupuestos"],
  ["otro", "Otros"],
  ["descartados", "Descartados"],
];

const ORIGENES = [
  ["todos", "Todos"],
  ["whatsapp", "📱 WhatsApp"],
  ["mail", "📧 Proveedores"],
];

/* El papel (si es imagen, el link firmado) vive en services/papelComprobante:
   lo usa también Proveedores › Facturas a pagar. Se re-exporta por quien lo
   importaba de acá. */
export { esImagen };

/* Un renglón del papel. Se le dice qué hacer con el hueco, porque el hueco
   significa algo distinto según por qué esté vacío. */
const Dato = ({ rotulo, valor, vacio = "no lo pude leer" }) => (
  <div className="cmp-dato">
    <span className="cmp-rotulo">{rotulo}</span>
    {valor === null || valor === undefined || valor === "" ? (
      <span className="tenue">{vacio}</span>
    ) : (
      <b>{valor}</b>
    )}
  </div>
);

const FacturasRemitos = () => {
  const { permisos = null } = useOutletContext() || {};
  const puede = (p) => permisos === null || permisos.includes(p);
  const puedeVerMail = puede("ver.comprobantes.todos");
  const puedeVer = puede("ver.comprobantes") || puedeVerMail;
  const puedeEditar = permisos !== null && permisos.includes("cargar.comprobantes");

  const [wa, setWa] = useState(undefined);
  const [mail, setMail] = useState(undefined);
  const [error, setError] = useState(null);
  const [origen, setOrigen] = useState("todos");
  const [tipo, setTipo] = useState("todos");
  const [busca, setBusca] = useState("");
  const [orden, setOrden] = useState(POR_LLEGADA);
  const [links, setLinks] = useState({}); // key -> { url, vence } (ver linkVigente)
  const [editando, setEditando] = useState(null);
  const [aviso, setAviso] = useState("");
  /* El estado en Proveedores › Facturas a pagar, por factura (key →
     chip). Si no se pudo traer, no hay chips y nada más cambia. */
  const [chips, setChips] = useState({});
  const [vuelta, setVuelta] = useState(0);
  /* La hora con la que se dice «Nuevo», «hoy» y «ayer». La pantalla queda
     abierta horas: se renueva sola cada minuto, para que lo de la tarde diga
     «ayer» pasada la medianoche aunque nadie toque nada. */
  const [ahora, setAhora] = useState(() => Date.now());

  useEffect(() => {
    const reloj = setInterval(() => setAhora(Date.now()), 60 * 1000);
    return () => clearInterval(reloj);
  }, []);

  useEffect(() => {
    let vivo = true;
    const firmar = (key, ruta, origen) => {
      if (!ruta) return;
      firmarPapel(origen, ruta).then((l) => vivo && setLinks((p) => ({ ...p, [key]: l })));
    };
    traerComprobantes()
      .then((filas) => {
        if (!vivo) return;
        setWa(filas);
        filas.forEach((f) => firmar(`whatsapp-${f.id}`, f.archivo, "whatsapp"));
      })
      .catch((e) => vivo && setError(e?.message || "No se pudieron leer."));
    if (puedeVerMail) {
      traerFacturas()
        .then((filas) => {
          if (!vivo) return;
          setMail(filas);
          filas.forEach((f) => firmar(`mail-${f.id}`, f.ruta, "mail"));
        })
        .catch((e) => vivo && setError(e?.message || "No se pudieron leer."));
    } else {
      setMail([]);
    }
    Promise.resolve(traerChips())
      .then((cs) => {
        if (!vivo || !Array.isArray(cs)) return;
        setChips(Object.fromEntries(cs.map((c) => [`${c.origen}-${c.ref_id}`, c])));
      })
      .catch(() => {});
    return () => { vivo = false; };
  }, [vuelta, puedeVerMail]);

  const recargar = () => { setEditando(null); setVuelta((v) => v + 1); };

  /* LOS LINKS SE FIRMAN AL ABRIR LA PANTALLA Y DURAN UNA HORA (ticket 455).
     El que ya no sirve se firma de nuevo cuando hace falta, no antes: firmar
     las cien cartas cada hora con la pestaña olvidada son cien pedidos para
     nada, y cada foto se volvería a bajar entera. */
  const renovar = async (r) => {
    const l = await firmarPapel(r.origen, r.archivo);
    if (l) setLinks((p) => ({ ...p, [r.key]: l }));
    return l;
  };

  /* Con el link vigente no se toca nada: lo abre el navegador, como siempre.
     Vencido, la pestaña se abre ACÁ, dentro del clic, y recién después se la
     manda al link nuevo: abierta cuando vuelve la firma, el navegador la toma
     por una ventana emergente y la bloquea. `opener = null` hace lo que hacía
     `rel="noreferrer"`: el papel no puede tocar la pestaña del hub.
     Como «Corregir» y «Marcarla», cada clic borra el aviso anterior: si no,
     «Tocá de nuevo el papel» o «No pude abrir el papel» quedan arriba después
     de que el papel se abrió bien. */
  const abrirPapel = (e, r) => {
    setAviso("");
    if (linkVigente(links[r.key]?.vence, Date.now())) return;
    e.preventDefault();
    const ventana = window.open("", "_blank");
    if (ventana) ventana.opener = null;
    renovar(r).then((l) => {
      if (!l) {
        if (ventana) ventana.close();
        setAviso("No pude abrir el papel. Recargá la página.");
      } else if (ventana) {
        ventana.location.href = l.url;
      } else {
        setAviso("Tocá de nuevo el papel para abrirlo.");
      }
    });
  };

  const tirar = async (f, fuera) => {
    setAviso("");
    try {
      await descartar(f.id, fuera);
      recargar();
    } catch (e) {
      setAviso(e?.message || "No se pudo guardar.");
    }
  };

  const marcarUsada = async (f) => {
    const usada = f.estado !== "usada";
    setAviso("");
    try {
      await marcar(f.id, usada);
      setMail((prev) =>
        (prev || []).map((x) =>
          x.id === f.id
            ? { ...x, estado: usada ? "usada" : "nuevo",
                usado_en: usada ? new Date().toISOString() : null }
            : x));
    } catch (e) {
      setAviso("No pude marcarla. " + (e?.message || ""));
    }
  };

  const cargando = wa === undefined || mail === undefined;

  const todo = useMemo(
    () => ordenar([
      ...(wa || []).map((f) => fila("whatsapp", f)),
      ...(mail || []).map((f) => fila("mail", f)),
    ], orden),
    [wa, mail, orden]);

  const visibles = useMemo(() => todo.filter((r) => {
    if (origen !== "todos" && r.origen !== origen) return false;
    if (tipo === "descartados") {
      if (!r.descartado) return false;
    } else {
      if (r.descartado) return false;
      if (tipo !== "todos" && r.tipo !== tipo) return false;
    }
    return coincide(r, busca);
  }), [todo, origen, tipo, busca]);

  if (!puedeVer) return <PuertaCerrada />;
  if (error) {
    return (
      <div className="panel-aviso"><h3>No se pudo consultar</h3></div>
    );
  }

  return (
    <div className="cmp">
      <div className="cmp-barra">
        <div className="cmp-tipos" role="group" aria-label="Origen">
          {ORIGENES.map(([id, label]) => (
            <button key={id} type="button"
                    className={"cmp-seg" + (origen === id ? " activo" : "")}
                    onClick={() => setOrigen(id)}>
              {label}
            </button>
          ))}
        </div>
        <div className="cmp-tipos" role="group" aria-label="Tipo">
          {TIPOS.map(([id, label]) => (
            <button key={id} type="button"
                    className={"cmp-seg" + (tipo === id ? " activo" : "")}
                    onClick={() => setTipo(id)}>
              {label}
            </button>
          ))}
        </div>
        <div className="cmp-tipos" role="group" aria-label="Orden">
          {ORDENES.map(([id, label]) => (
            <button key={id} type="button" aria-pressed={orden === id}
                    className={"cmp-seg" + (orden === id ? " activo" : "")}
                    onClick={() => setOrden(id)}>
              {label}
            </button>
          ))}
        </div>
        <input type="search" className="cmp-busca"
               placeholder="Proveedor, número, CUIT, quién…"
               aria-label="Buscar facturas y remitos"
               value={busca} onChange={(e) => setBusca(e.target.value)} />
      </div>

      {aviso && <p className="cmp-aviso">{aviso}</p>}

      {cargando && <p className="tenue">Buscando…</p>}

      {!cargando && visibles.length === 0 && (
        <div className="panel-aviso suave">
          <h3>{todo.length === 0 ? "Todavía no hay ninguna" : "Nada con ese filtro"}</h3>
          <p className="panel-aviso__chico">
            {todo.length === 0
              ? "Mandale la foto a Cepi por WhatsApp, o pedile al proveedor que "
                + "mande la factura a cepi@fibot.ar, y aparece acá."
              : "Probá con otro origen o tipo, o borrá lo que buscaste."}
          </p>
        </div>
      )}

      {visibles.map((r) => {
        const f = r.raw;
        const esWA = r.origen === "whatsapp";
        const rotulo = ROTULO_TIPO[r.tipo] || "Otro";
        const dudas = Array.isArray(f.dudas) ? f.dudas : [];
        const usada = !esWA && f.estado === "usada";
        /* «Sin leer» y «no la pude leer» los define `shared/facturasMail`
           (por `leido_en` y `lectura_error`), no esta pantalla: mirando
           `leido_por`, una ya leída que no anotó con qué salía «sin leer». */
        const sinLeer = !esWA && lectura(f) === SIN_LEER;
        const noPude = !esWA && lectura(f) === NO_PUDE;
        /* Un «no lo pude leer» sobre una factura que nadie miró todavía manda
           a abrir el PDF para nada: mientras no se leyó, dice eso. */
        const vacio = sinLeer ? "todavía no la leí" : undefined;
        const llego = cuandoLlego(r.llego, ahora);
        const chip = esWA
          ? <span className="pill" title="Lo mandaste por WhatsApp">📱 por WhatsApp</span>
          : <span className="pill" title="La mandó un proveedor por mail">📧 de proveedor</span>;
        /* Sólo en las facturas: un remito no se paga. */
        const pago = r.tipo === "factura" ? chipDe(chips[r.key]) : null;
        const estadoPago = r.tipo === "factura" ? chips[r.key]?.estado : null;
        return (
          <div className="cmp-carta" key={r.key}>
            <div className="cmp-papel">
              {links[r.key] ? (
                <a href={links[r.key].url} target="_blank" rel="noreferrer"
                   onClick={(e) => abrirPapel(e, r)}
                   onAuxClick={(e) => e.button === 1 && abrirPapel(e, r)}>
                  {esImagen(r.origen, f) ? (
                    /* Una foto que se dibuja por primera vez con el link
                       vencido (la de un descartado, que no estaba en la
                       lista) es una imagen rota: se firma de nuevo. Con el
                       link vigente, el error es otro y no se insiste. */
                    <img src={links[r.key].url}
                         onError={() => !linkVigente(links[r.key]?.vence, Date.now()) && renovar(r)}
                         alt={`${rotulo} de ${f.proveedor || "proveedor sin leer"}`} />
                  ) : (
                    <span className="cmp-sinpapel">Abrir el papel
                      <small>Se abre en otra pestaña</small></span>
                  )}
                </a>
              ) : r.archivo ? (
                <span className="tenue">Abriendo el archivo…</span>
              ) : (
                <span className="cmp-sinpapel">Sin archivo guardado
                  <small>Quedaron sólo los datos</small></span>
              )}
            </div>

            <div className="cmp-datos">
              <div className="cmp-cabeza">
                <b>{rotulo}</b>
                {chip}
                {esNuevo(r.llego, ahora) && (
                  <span className="pill info" title={`Llegó ${llego}`}>Nuevo</span>)}
                {f.proveedor ? ` · ${f.proveedor}` : ""}
                {esWA && f.alcance === "privado" && <span className="pill gris">🔒 privada</span>}
                {!r.archivo && <span className="pill mal">sin archivo</span>}
                {r.descartado && <span className="pill gris">descartado</span>}
                {esWA && f.revisado_en && (
                  <span className="pill ok" title={f.revisado_por || ""}>✓ revisado</span>)}
                {pago && <span className={"pill " + pago.clase} title={pago.title}>{pago.texto}</span>}
                {usada && (
                  <span className="pill gris"
                        title="Marcada como usada en esta pantalla. No es el pago: eso está en Proveedores › Facturas a pagar.">
                    ✓ usada
                  </span>)}
                {sinLeer && <span className="pill gris">⏳ sin leer</span>}
                {noPude && <span className="pill mal">⚠️ no la pude leer</span>}
              </div>

              {noPude ? (
                <p className="cmp-dudas">No se pudo leer.</p>
              ) : (
                <>
                  <Dato rotulo="Proveedor" valor={f.proveedor} vacio={vacio} />
                  <Dato rotulo="CUIT" valor={f.cuit} vacio={vacio} />
                  <Dato rotulo="Fecha" valor={dia(f.fecha)} vacio={vacio} />
                  <Dato rotulo="Número" valor={f.numero} vacio={vacio} />
                  {!(r.tipo === "remito" && f.importe === null) && (
                    <Dato rotulo="Importe" valor={plata(f.importe, f.moneda)}
                          vacio={vacio} />)}
                </>
              )}

              {f.detalle && <p className="cmp-detalle">{f.detalle}</p>}
              {pago?.linea && <p className="cmp-detalle">{pago.linea}</p>}
              {dudas.length > 0 && (
                <p className="cmp-dudas">⚠️ El bot no está seguro de: {dudas.map(rotuloDeCampo).join(", ")}.</p>)}

              {esWA ? (
                <p className="cmp-pie tenue">
                  {f.quien_nombre || "alguien"} · llegó {llego}
                  {f.leido_por ? ` · ${etiquetaLeidoPor(f.leido_por)}` : ""}
                  {f.revisado_por ? ` · corrigió ${f.revisado_por}` : ""}
                </p>
              ) : (
                <p className="cmp-pie tenue">
                  La mandó {f.remitente || f.remitente_email || "—"} ·
                  {" "}llegó {llego}
                  {f.usado_en ? ` · usada ${cuando(f.usado_en)}` : ""}
                  {f.leido_por ? ` · ${etiquetaLeidoPor(f.leido_por)}` : ""}
                </p>
              )}

              {esWA && puedeEditar && editando !== r.key && (
                <div className="cmp-acciones">
                  <button type="button" className="tk-boton tk-boton--chico"
                          onClick={() => { setAviso(""); setEditando(r.key); }}>
                    ✎ Corregir
                  </button>
                  {r.descartado ? (
                    <button type="button" className="tk-boton tk-boton--chico tk-boton--flojo"
                            onClick={() => tirar(f, false)}>↩ Devolver al cajón</button>
                  ) : (
                    <button type="button" className="tk-boton tk-boton--chico tk-boton--flojo"
                            onClick={() => tirar(f, true)}>No es un comprobante</button>
                  )}
                </div>
              )}
              {/* Qué le pasa en Proveedores si la corrigen o la descartan. */}
              {esWA && puedeEditar && avisoAlCorregir(estadoPago) && (
                <p className="cmp-pie tenue">{avisoAlCorregir(estadoPago)}</p>
              )}
              {esWA && puedeEditar && editando === r.key && (
                <Corregir f={f} onListo={recargar} onCancelar={() => setEditando(null)} />
              )}

              {!esWA && puedeEditar && (
                <p className="cmp-pie">
                  <button type="button" className="cmp-seg" onClick={() => marcarUsada(f)}>
                    {usada ? "Volver a marcarla nueva" : "Marcarla como usada"}
                  </button>
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FacturasRemitos;
